// Global AI Assistant with Expandable Chat Interface
import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Fab,
  Paper,
  IconButton,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Collapse,
  Badge,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  Button,
  Alert,
  Stack,
  Zoom,
  Fade,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Link
} from '@mui/material';
import {
  SmartToy as AIIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Person as PersonIcon,
  HelpOutline as HelpIcon,
  Code as CodeIcon,
  Navigation as NavigateIcon,
  Search as SearchIcon,
  Psychology as ThinkingIcon,
  Article as DocIcon,
  History as HistoryIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import conversationService, {
  ConversationMessage,
  ConversationState
} from '../services/conversationService';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface AIResponse extends ConversationMessage {
  mode?: 'help' | 'configure' | 'navigate' | 'research';
  action?: {
    type: 'navigate' | 'open_doc' | 'select_template';
    target?: string;
    params?: any;
  };
  documentation?: Array<{
    title: string;
    url: string;
    snippet: string;
    version?: string;
  }>;
  version_info?: {
    target_version: string;
    compatibility: string;
    warnings?: string[];
  };
}

interface PageContext {
  path: string;
  page: string;
  data?: any;
}

const GlobalAIAssistant: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { getToken, isLoaded: authLoaded } = useAuth();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  
  // UI State
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Chat State
  const [messages, setMessages] = useState<AIResponse[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMode, setCurrentMode] = useState<'help' | 'configure' | 'navigate' | 'research'>('help');
  
  // Page Context
  const [pageContext, setPageContext] = useState<PageContext>({ path: '', page: '' });
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get page context from current location
  useEffect(() => {
    const getPageName = (path: string): string => {
      const pageMap: { [key: string]: string } = {
        '/dashboard': 'Dashboard',
        '/templates/create': 'Template Creation',
        '/templates/library': 'Template Library',
        '/configurations': 'Configuration Management',
        '/conversational': 'AI Configuration Builder',
        '/validation': 'Network Validation',
        '/bulk-operations': 'Bulk Operations',
        '/audit': 'Audit & Compliance',
        '/export-deploy': 'Export & Deploy',
        '/analytics': 'Analytics'
      };
      return pageMap[path] || 'Unknown Page';
    };

    setPageContext({
      path: location.pathname,
      page: getPageName(location.pathname),
      data: location.state
    });
  }, [location]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (authLoaded && orgLoaded && userLoaded && !isConnected) {
      console.log('[GlobalAI] All loaded, user object:', user);
      console.log('[GlobalAI] User firstName:', user?.firstName);
      console.log('[GlobalAI] User fullName:', user?.fullName);
      console.log('[GlobalAI] User email:', user?.primaryEmailAddress?.emailAddress);
      connectToAssistant();
    }
    
    return () => {
      if (isConnected) {
        conversationService.disconnect();
      }
    };
  }, [authLoaded, orgLoaded, userLoaded]);

  // Set up event handlers
  useEffect(() => {
    const handleMessage = (msg: ConversationMessage) => {
      const aiMsg = msg as AIResponse;
      console.log('[GlobalAI] Received message:', aiMsg);
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
      
      // Update unread count if chat is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
      
      // Navigation is now handled via WebSocket navigation_execute messages
      // No need to handle navigation actions here anymore
    };

    const handleConnectionChange = (connected: boolean) => {
      console.log('[GlobalAI] Connection state changed:', connected);
      setIsConnected(connected);
      
      // Send context update when connection is established
      if (connected && user) {
        setTimeout(() => {
          console.log('[GlobalAI] Sending delayed context update after connection');
          sendContextUpdate();
        }, 500); // Small delay to ensure connection is stable
      }
    };

    const unsubscribeMessage = conversationService.onMessage(handleMessage);
    const unsubscribeConnection = conversationService.onConnectionChange(handleConnectionChange);
    
    return () => {
      unsubscribeMessage();
      unsubscribeConnection();
    };
  }, [isOpen, user, currentMode, pageContext, organization]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, isMinimized]);

  // Clear unread count when opening
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const connectToAssistant = async () => {
    try {
      setIsLoading(true);
      const token = await getToken();
      if (token) {
        sessionStorage.setItem('auth_token', token);
        await conversationService.connect(undefined, token);
        
        // Don't send context immediately - wait for connection to stabilize
        // Context will be sent in the connection handler effect
      }
    } catch (error) {
      console.error('Failed to connect AI assistant:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendContextUpdate = () => {
    // Extract user's name with better fallbacks
    let userName = user?.firstName;
    
    // If no first name, try to extract from full name
    if (!userName && user?.fullName) {
      const nameParts = user.fullName.split(' ');
      userName = nameParts[0];
    }
    
    // If still no name, try to extract from email
    if (!userName && user?.primaryEmailAddress?.emailAddress) {
      const emailName = user.primaryEmailAddress.emailAddress.split('@')[0];
      // Handle dots in email (carl.amess -> Carl)
      const firstName = emailName.includes('.') ? emailName.split('.')[0] : emailName;
      // Capitalize first letter
      userName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    
    // Final fallback
    if (!userName) {
      userName = user?.username || 'User';
    }
    
    console.log('[GlobalAI] Sending context update with userName:', userName);
    console.log('[GlobalAI] Current mode:', currentMode);
    
    const contextMessage = {
      type: 'context_update',
      page_context: pageContext,
      organization_id: organization?.id,
      mode: currentMode,
      user_name: userName,
      user_email: user?.primaryEmailAddress?.emailAddress
    };
    conversationService.sendRawMessage(contextMessage);
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim() || !isConnected) return;
    
    // Add user message to chat
    const userMessage: AIResponse = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Extract user's name with same logic as context update
    let userName = user?.firstName;
    
    if (!userName && user?.fullName) {
      const nameParts = user.fullName.split(' ');
      userName = nameParts[0];
    }
    
    if (!userName && user?.primaryEmailAddress?.emailAddress) {
      const emailName = user.primaryEmailAddress.emailAddress.split('@')[0];
      // Handle dots in email (carl.amess -> Carl)
      const firstName = emailName.includes('.') ? emailName.split('.')[0] : emailName;
      userName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    
    if (!userName) {
      userName = user?.username || 'User';
    }
    
    console.log('[GlobalAI] Sending message with userName:', userName, 'mode:', currentMode);
    
    // Send message with context and user info
    const messageData = {
      type: 'message',
      content: inputMessage,
      page_context: pageContext,
      mode: currentMode,
      user_name: userName,
      user_email: user?.primaryEmailAddress?.emailAddress
    };
    conversationService.sendRawMessage(messageData);
    
    // Clear input and show typing indicator
    setInputMessage('');
    setIsTyping(true);
    inputRef.current?.focus();
  };

  const handleNavigation = (target: string, params?: any) => {
    // Direct navigation without confirmation
    // Confirmation is handled through conversation flow
    navigate(target, { state: params });
  };

  const handleModeChange = (mode: 'help' | 'configure' | 'navigate' | 'research') => {
    console.log('[GlobalAI] Mode changed to:', mode);
    setCurrentMode(mode);
    
    // Extract user name with better logic
    let userName = user?.firstName;
    
    if (!userName && user?.fullName) {
      const nameParts = user.fullName.split(' ');
      userName = nameParts[0];
    }
    
    if (!userName && user?.primaryEmailAddress?.emailAddress) {
      const emailName = user.primaryEmailAddress.emailAddress.split('@')[0];
      // Handle dots in email (carl.amess -> Carl)
      const firstName = emailName.includes('.') ? emailName.split('.')[0] : emailName;
      userName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    
    if (!userName) {
      userName = user?.username || 'User';
    }
    
    const contextMessage = {
      type: 'context_update',
      page_context: pageContext,
      organization_id: organization?.id,
      mode: mode,
      user_name: userName,
      user_email: user?.primaryEmailAddress?.emailAddress
    };
    conversationService.sendRawMessage(contextMessage);
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'help': return <HelpIcon />;
      case 'configure': return <CodeIcon />;
      case 'navigate': return <NavigateIcon />;
      case 'research': return <SearchIcon />;
      default: return <AIIcon />;
    }
  };

  const getModeColor = (mode: string) => {
    switch (mode) {
      case 'help': return 'info';
      case 'configure': return 'success';
      case 'navigate': return 'warning';
      case 'research': return 'secondary';
      default: return 'primary';
    }
  };

  const renderMessage = (message: AIResponse) => {
    const isUser = message.role === 'user';
    
    return (
      <ListItem 
        alignItems="flex-start" 
        sx={{ 
          flexDirection: isUser ? 'row-reverse' : 'row',
          mb: 1 
        }}
      >
        <ListItemAvatar sx={{ 
          minWidth: isUser ? 0 : 40, 
          ml: isUser ? 1 : 0,
          mr: isUser ? 0 : 1 
        }}>
          <Avatar sx={{ 
            bgcolor: isUser ? theme.palette.primary.main : theme.palette.grey[700],
            width: 32,
            height: 32
          }}>
            {isUser ? <PersonIcon fontSize="small" /> : <AIIcon fontSize="small" />}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: isUser ? 'flex-end' : 'flex-start',
              mb: 0.5
            }}>
              <Typography variant="caption" color="text.secondary">
                {isUser ? 'You' : 'AI Assistant'}
              </Typography>
              {message.mode && !isUser && (
                <Chip 
                  icon={getModeIcon(message.mode)}
                  label={message.mode}
                  size="small"
                  color={getModeColor(message.mode) as any}
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </Box>
          }
          secondary={
            <Paper 
              elevation={1} 
              sx={{ 
                p: 1.5,
                bgcolor: isUser ? theme.palette.primary.light + '20' : theme.palette.grey[50],
                maxWidth: '85%',
                ml: isUser ? 'auto' : 0,
                mr: isUser ? 0 : 'auto'
              }}
            >
              <Typography variant="body2" component="div">
                {message.content}
              </Typography>
              
              {/* Documentation References */}
              {message.documentation && message.documentation.length > 0 && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Divider />
                  <Typography variant="caption" color="text.secondary">
                    📚 Documentation References:
                  </Typography>
                  {message.documentation.map((doc, idx) => (
                    <Card key={idx} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="subtitle2">{doc.title}</Typography>
                        <Typography variant="caption" color="text.secondary" paragraph>
                          {doc.snippet}
                        </Typography>
                        <Button 
                          size="small" 
                          startIcon={<DocIcon />}
                          onClick={() => window.open(doc.url, '_blank')}
                        >
                          View Documentation
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
              
              {/* Version Information */}
              {message.version_info && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    Target Version: {message.version_info.target_version}
                  </Typography>
                  {message.version_info.warnings?.map((warning, idx) => (
                    <Typography key={idx} variant="caption" display="block" color="warning.main">
                      ⚠️ {warning}
                    </Typography>
                  ))}
                </Alert>
              )}
            </Paper>
          }
        />
      </ListItem>
    );
  };

  return (
    <>
      {/* Floating Action Button */}
      <Zoom in={!isOpen}>
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: isMobile ? 16 : 24,
            right: isMobile ? 16 : 24,
            zIndex: theme.zIndex.fab
          }}
          onClick={() => setIsOpen(true)}
        >
          <Badge badgeContent={unreadCount} color="error">
            <AIIcon />
          </Badge>
        </Fab>
      </Zoom>

      {/* Expandable Chat Window */}
      <Fade in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 24,
            right: isMobile ? 0 : 24,
            width: isMobile ? '100%' : 400,
            height: isMobile ? '100%' : 600,
            maxHeight: isMobile ? '100vh' : '80vh',
            display: isOpen ? 'flex' : 'none',
            flexDirection: 'column',
            zIndex: theme.zIndex.modal,
            borderRadius: isMobile ? 0 : 2
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AIIcon sx={{ mr: 1 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {pageContext.page} • {isConnected ? 'Connected' : 'Connecting...'}
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => setIsMinimized(!isMinimized)}
              >
                <MinimizeIcon />
              </IconButton>
              <IconButton
                size="small"
                sx={{ color: 'white', ml: 1 }}
                onClick={() => setIsOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Mode Selector */}
          <Box sx={{ p: 1, bgcolor: theme.palette.grey[100] }}>
            <Stack direction="row" spacing={1} justifyContent="center">
              {(['help', 'configure', 'navigate', 'research'] as const).map(mode => (
                <Chip
                  key={mode}
                  icon={getModeIcon(mode)}
                  label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                  onClick={() => handleModeChange(mode)}
                  color={currentMode === mode ? getModeColor(mode) as any : 'default'}
                  variant={currentMode === mode ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Box>

          {/* Messages Area */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0  // Important for flex containers
          }}>
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 2,
                bgcolor: theme.palette.grey[50],
                minHeight: 0,  // Important for scroll
                '&::-webkit-scrollbar': {
                  width: '10px',  // Made wider for visibility
                  display: 'block !important',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.2)',  // Made darker
                  borderRadius: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.primary.main,  // Use primary color for visibility
                  borderRadius: '5px',
                  border: '2px solid transparent',
                  backgroundClip: 'content-box',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                // Firefox scrollbar styling
                scrollbarWidth: 'thin',
                scrollbarColor: `${theme.palette.primary.main} rgba(0,0,0,0.2)`,
              }}
            >
              {isLoading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Connecting to AI Assistant...
                  </Typography>
                </Box>
              )}
              
              {!isLoading && messages.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ThinkingIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Hello{user?.firstName ? `, ${user.firstName}` : ''}! I'm your AI assistant. I can help you with:
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 2, px: 4 }}>
                    <Chip icon={<HelpIcon />} label="Answer questions about the system" size="small" />
                    <Chip icon={<CodeIcon />} label="Create network configurations" size="small" />
                    <Chip icon={<NavigateIcon />} label="Navigate to features" size="small" />
                    <Chip icon={<SearchIcon />} label="Search vendor documentation" size="small" />
                  </Stack>
                </Box>
              )}
              
              <List sx={{ p: 0 }}>
                {messages.map((message, index) => (
                  <React.Fragment key={index}>
                    {renderMessage(message)}
                  </React.Fragment>
                ))}
              </List>
              
              {isTyping && (
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    AI is thinking...
                  </Typography>
                </Box>
              )}
              
              <div ref={messagesEndRef} />
            </Box>
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                ref={inputRef}
                fullWidth
                size="small"
                placeholder={`Ask me anything... (${currentMode} mode)`}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={!isConnected || isMinimized}
                multiline
                maxRows={3}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!isConnected || !inputMessage.trim() || isMinimized}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </>
  );
};

export default GlobalAIAssistant;